import { personas, type PersonaSlug } from '@/lib/personas';
import { PersonaFilterChip } from '@/components/PersonaFilterChip';
import { cn } from '@/lib/utils';

export interface RoleFilterProps {
  selected: PersonaSlug[];
  onChange: (next: PersonaSlug[]) => void;
  /** Restrict the chips to roles that have content here. Omit to show all. */
  available?: PersonaSlug[];
  className?: string;
}

/**
 * The "All roles" + role chips row, shared by the command palette and the
 * listing pages. "All roles" is active when none of the shown roles are
 * selected (no filter); selecting roles narrows and ORs together; clearing
 * falls back to All, so there is no empty-selection dead end.
 *
 * When `available` is given, only those roles get a chip and the All state
 * and Clear action work over that set, so a role selected on another page
 * (and not tagged here) sits inert rather than emptying this list.
 */
export function RoleFilter({
  selected,
  onChange,
  available,
  className,
}: RoleFilterProps) {
  const shown = available
    ? personas.filter((p) => available.includes(p.slug))
    : personas;
  const shownSlugs = shown.map((p) => p.slug);
  const showingAll = !shownSlugs.some((s) => selected.includes(s));
  const clearShown = () => onChange(selected.filter((s) => !shownSlugs.includes(s)));
  const toggle = (slug: PersonaSlug) =>
    onChange(
      selected.includes(slug)
        ? selected.filter((s) => s !== slug)
        : [...selected, slug],
    );

  if (shown.length === 0) return null;

  return (
    <div
      role="group"
      aria-label="Filter by role"
      className={cn('flex flex-wrap gap-2', className)}
    >
      <button
        type="button"
        role="checkbox"
        aria-checked={showingAll}
        aria-label="Show all roles"
        onClick={clearShown}
        className={cn(
          'inline-flex h-[30px] items-center gap-1.5 rounded-full border pl-2.5 pr-3 font-sans',
          'transition-[border-color,background-color,color] duration-150 ease-out',
          'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:[outline-color:hsl(var(--accent-ring))]',
          showingAll
            ? 'border-rule-strong bg-bg-mute text-ink shadow-[inset_0_1px_0_hsl(var(--rule)/0.6)]'
            : 'border-rule bg-bg-elev text-ink-soft hover:border-rule-strong',
        )}
      >
        <span
          aria-hidden="true"
          className={cn(
            'inline-block h-3.5 w-[4px] shrink-0 rounded-sm',
            showingAll ? 'bg-ink-soft' : 'bg-rule-strong',
          )}
        />
        <span
          className="whitespace-nowrap text-[13px] font-medium"
          style={{ letterSpacing: '-0.005em' }}
        >
          All roles
        </span>
      </button>
      {shown.map((p) => (
        <PersonaFilterChip
          key={p.slug}
          persona={p.slug}
          selected={selected.includes(p.slug)}
          onToggle={() => toggle(p.slug)}
        />
      ))}
    </div>
  );
}

export default RoleFilter;
